import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Coords, ElementSize, Page } from 'editor/src/store/design/types';

import useEditorModeTranslations from 'editor/src/util/useEditorModeTranslations';

import { CanvasRotation } from 'editor/src/component/EditorArea/types';
import useEmptyPlaceholderEventHandlers from 'editor/src/component/EditorArea/useEmptyPlaceholderEventHandlers';
import { UploaderContext } from 'editor/src/component/Uploader/useUploader';

import EmptyPlaceholderIcon from './EmptyPlaceholderIcon';
import usePlaceholderPositioning from './usePlaceholderPositioning';

import styles from './EmptyContentPlaceholderLegacy.module.scss';

interface Props {
  spreadCoords: Coords;
  content: ElementSize;
  canvasRotation: CanvasRotation;
  page: Page;
}

function EmptyContentPlaceholderLegacy({ content, spreadCoords, canvasRotation, page }: Props) {
  const { placeholderContainerRef, placeholderSize, position, isDragging, isMobile } = usePlaceholderPositioning(
    content,
    spreadCoords,
    canvasRotation,
    page,
  );

  const uploader = useContext(UploaderContext);
  const { t } = useTranslation();
  const { galleryTab } = useEditorModeTranslations();

  const horizontalPadding = useMemo(() => placeholderSize * 0.1, [placeholderSize]);

  const { onPlaceholderClick } = useEmptyPlaceholderEventHandlers(uploader);

  return (
    <div
      className={styles.placeholderContainer}
      ref={placeholderContainerRef}
      style={{
        width: `${placeholderSize}px`,
        height: `${placeholderSize}px`,
        padding: `0 ${horizontalPadding}px`,
        transform: `translate3d(${position.left}px,${position.top}px,0) scale(${position.zoom})`,
        pointerEvents: isDragging ? 'none' : 'auto',
      }}
      onClick={() => onPlaceholderClick(true)}
    >
      <EmptyPlaceholderIcon className={styles.icon} />
      <span className={styles.title} style={{ fontSize: placeholderSize / 13 }}>
        {t(isMobile ? 'Tap to add your design here' : 'Drop your design here')}
      </span>
      <span className={styles.subtitle} style={{ fontSize: placeholderSize / 20 }}>
        {isMobile
          ? t('or-dnd-from-files', { files: galleryTab })
          : t('or-use-add-new-button', { 'add-new': t('editor-add-layer') })}
      </span>
    </div>
  );
}

export default React.memo(EmptyContentPlaceholderLegacy);
